import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface Integration {
  id: string;
  name: string;
  type: string;
  provider: string;
  active: boolean;
  lastSyncAt: string | null;
  syncCount: number;
}

const integrationStatusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Ativa', color: 'bg-green-100 text-green-700' },
  INACTIVE: { label: 'Inativa', color: 'bg-gray-100 text-gray-700' },
  ERROR: { label: 'Erro', color: 'bg-red-100 text-red-700' },
  SYNCING: { label: 'Sincronizando', color: 'bg-yellow-100 text-yellow-700' },
};

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    api.get('/integrations').then((r) => {
      setIntegrations(r.data);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, []);

  const testConnection = async (id: string) => {
    try {
      await api.post(`/integrations/${id}/test`);
      alert('Conexao testada com sucesso!');
    } catch {
      alert('Falha ao testar conexao');
    }
  };

  const sync = async (id: string) => {
    try {
      await api.post(`/integrations/${id}/sync`);
      alert('Sincronizacao iniciada!');
      api.get('/integrations').then((r) => setIntegrations(r.data));
    } catch {
      alert('Falha ao sincronizar');
    }
  };

  return loadingData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations.map((integration) => (
            <div key={integration.id} className="bg-white rounded-xl p-6 shadow-sm border border-dark-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-dark-900">{integration.name}</h3>
                  <p className="text-sm text-dark-500">{integration.provider}</p>
                </div>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${integrationStatusLabels[integration.active ? 'ACTIVE' : 'INACTIVE']?.color || 'bg-gray-100 text-gray-700'}`}>
                  {integration.active ? 'Ativa' : 'Inativa'}
                </span>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-dark-500">Tipo</span>
                  <span className="text-dark-700 font-medium">{integration.type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-dark-500">Ultima Sincronizacao</span>
                  <span className="text-dark-700">
                    {integration.lastSyncAt
                      ? new Date(integration.lastSyncAt).toLocaleDateString('pt-BR')
                      : 'Nunca'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-dark-500">Sincronizacoes</span>
                  <span className="text-dark-700 font-medium">{integration.syncCount}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-dark-100 flex gap-3">
                <button
                  onClick={() => testConnection(integration.id)}
                  className="flex-1 px-3 py-2 border border-dark-200 rounded-lg text-xs font-medium text-dark-700 hover:bg-dark-50 transition-colors"
                >
                  Testar
                </button>
                <button
                  onClick={() => sync(integration.id)}
                  className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors"
                >
                  Sincronizar
                </button>
              </div>
            </div>
          ))}
          {integrations.length === 0 && (
            <div className="col-span-full text-center py-12 text-dark-500">Nenhuma integracao encontrada</div>
          )}
        </div>
  );
}
